import { TireHotelLocationRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { getLongAddressName, useBookingActions } from 'noddi-web';
import { useState } from 'react';
import { TireHotelLocationSuggestionDialog } from './TireHotelLocationSuggestionDialog';

type Props = {
  /** Custom pickup address still awaiting a decision (`getPickupAddressAwaitingTireHotelDecision`). */
  addressId: number;
  /** Customer confirmed a known location — switch the pickup to it. */
  onSelectLocation: (location: TireHotelLocationRecord) => void;
};

/**
 * Asks "Is this where your tires are stored?" when a typed pickup address sits at a known tire hotel location (backend
 * `nearAddressIds`: same site, so a mistyped postal code still matches). Renders nothing when there is no match or the
 * lookup fails — booking creation re-checks the address and prompts again, so this is an earlier prompt, not the gate.
 */
export const TireHotelPickupAddressSuggestion = ({ addressId, onSelectLocation }: Props) => {
  const { setTireHotelPickupDecision } = useBookingActions();
  const [dismissedAddressId, setDismissedAddressId] = useState<number>();

  const { data: nearbyLocations } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTireHotelLocations,
    input: { nearAddressIds: [addressId], pageIndex: 0, pageSize: 5 }
  });

  const locations = nearbyLocations?.results ?? [];
  if (locations.length === 0 || dismissedAddressId === addressId) {
    return null;
  }

  return (
    <TireHotelLocationSuggestionDialog
      suggestions={locations.map((location) => ({
        tireHotelLocationId: location.id,
        name: location.name,
        address: getLongAddressName(location.address)
      }))}
      isPending={false}
      onAnswer={(decision) => {
        const confirmed =
          'tireHotelLocationId' in decision && locations.find(({ id }) => id === decision.tireHotelLocationId);
        if (confirmed) {
          onSelectLocation(confirmed);
        } else {
          setTireHotelPickupDecision(decision);
        }
      }}
      onDismiss={() => setDismissedAddressId(addressId)}
    />
  );
};
